import type { MeetingTemplateEditorLabels, MeetingTemplateListLabels } from '@wundo/ui';
import { m } from '@/paraglide/messages.js';

/*
 * Naming a default is the staff console's call, so the app never offers it.
 * The list is split by kind, so a default needs no kind of its own.
 */
export type AppMeetingTemplateLabels = Omit<
  MeetingTemplateListLabels,
  'makeDefault' | 'rowActions' | 'defaultMarkers'
> &
  MeetingTemplateEditorLabels;

/** Every text the shared template screens show, in the reader's language. */
export function meetingTemplateLabels(): AppMeetingTemplateLabels {
  return {
    newTemplate: m.settings_templates_new(),
    defaultMarker: m.settings_templates_default_marker(),
    personalMarker: m.settings_templates_personal_marker(),
    kinds: {
      one_on_one: m.meeting_kind_one_on_one(),
      team: m.meeting_kind_team(),
      other: m.conversations_kind_other(),
    },
    move: ({ name }) => m.settings_templates_move({ name }),
    dragInstructions: m.settings_templates_drag_instructions(),
    dragStart: (place) => m.settings_templates_drag_start(place),
    dragOver: (place) => m.settings_templates_drag_over(place),
    dragEnd: (place) => m.settings_templates_drag_end(place),
    dragCancel: ({ name }) => m.settings_templates_drag_cancel({ name }),

    back: m.settings_templates_back(),
    readOnlyNote: m.settings_template_readonly(),
    title: m.settings_template_title(),
    nameLabel: m.settings_template_name_label(),
    namePlaceholder: m.settings_template_name_placeholder(),
    descriptionLabel: m.settings_template_description_label(),
    descriptionHint: m.settings_template_description_desc(),
    descriptionPlaceholder: m.settings_template_description_placeholder(),
    colorLabel: m.settings_template_color_label(),
    colorHint: m.settings_template_color_desc(),
    kindLabel: m.settings_template_kind_label(),
    kindHint: m.settings_template_kind_desc(),
    scopeLabel: m.settings_template_scope_label(),
    scopeHint: m.settings_template_scope_desc(),
    scopes: {
      organisation: m.settings_template_scope_organisation(),
      personal: m.settings_template_scope_personal(),
    },
    colors: {
      navy: m.settings_template_color_navy(),
      green: m.settings_template_color_green(),
      pink: m.settings_template_color_pink(),
      purple: m.settings_template_color_purple(),
      teal: m.settings_template_color_teal(),
      grey: m.settings_template_color_grey(),
    },
    audienceLabel: m.settings_template_audience_label(),
    audienceHint: m.settings_template_audience_desc(),
    audienceNone: m.settings_template_audience_none(),
    audienceWholeTrack: ({ title }) =>
      m.settings_template_audience_whole_track({ title }),
    audienceOnlyCohort: ({ title }) =>
      m.settings_template_audience_only_cohort({ title }),
    audienceOtherCohorts: m.settings_template_audience_other_cohorts(),
    audienceDefaultNote: m.settings_template_audience_default_note(),
    topicsTitle: m.settings_template_topics_title(),
    topicsHint: m.settings_template_topics_desc(),
    topicTitleLabel: ({ position }) =>
      m.settings_template_topic_title_label({ position }),
    topicTitlePlaceholder: m.settings_template_topic_title_placeholder(),
    topicDescriptionLabel: ({ position }) =>
      m.settings_template_topic_description_label({ position }),
    topicDescriptionPlaceholder: m.settings_template_topic_description_placeholder(),
    addTopic: m.settings_template_topic_add(),
    moveTopic: ({ position }) => m.settings_template_topic_move({ position }),
    removeTopic: ({ position }) => m.settings_template_topic_remove({ position }),
    topicDragInstructions: m.settings_template_topic_drag_instructions(),
    topicDragStart: ({ position }) => m.settings_template_topic_drag_start({ position }),
    topicDragOver: (place) => m.settings_template_topic_drag_over(place),
    topicDragEnd: (place) => m.settings_template_topic_drag_end(place),
    topicDragCancel: m.settings_template_topic_drag_cancel(),
    save: m.settings_template_save(),
    create: m.settings_template_create(),
    delete: m.settings_template_delete(),
    deleteTitle: m.settings_template_delete_title(),
    deleteDescription: ({ name }) => m.settings_template_delete_description({ name }),
    deleteConfirm: m.settings_template_delete_confirm(),
    deleteCancel: m.settings_template_delete_cancel(),
  };
}
